import { create } from 'zustand';
import type {
  FeatureFlags,
  Organization,
  OrgBranding,
  OrgSettings,
} from '@/shared/types/common.types';

export const DEFAULT_SETTINGS: OrgSettings = {
  locale: 'en',
  timezone: 'UTC',
  date_format: 'YYYY-MM-DD',
  number_format: 'en-US',
  currency: 'USD',
  default_response_sla_days: 14,
  default_review_frequency: 'quarterly',
  auto_populate_lookback_months: 3,
  brand_primary_color: '#1F3A5F',
  brand_accent_color: '#0F766E',
  brand_header_text_color: '#FFFFFF',
  report_footer_text: 'Confidential',
};

/** Normalize partial/empty settings JSONB into a complete OrgSettings object */
export function normalizeSettings(raw: unknown): OrgSettings {
  const obj = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    locale: (typeof obj.locale === 'string' && obj.locale) || DEFAULT_SETTINGS.locale,
    timezone: (typeof obj.timezone === 'string' && obj.timezone) || DEFAULT_SETTINGS.timezone,
    date_format: (typeof obj.date_format === 'string' && obj.date_format) || DEFAULT_SETTINGS.date_format,
    number_format: (typeof obj.number_format === 'string' && obj.number_format) || DEFAULT_SETTINGS.number_format,
    currency: (typeof obj.currency === 'string' && obj.currency) || DEFAULT_SETTINGS.currency,
    default_response_sla_days: (typeof obj.default_response_sla_days === 'number' && obj.default_response_sla_days > 0) ? obj.default_response_sla_days : DEFAULT_SETTINGS.default_response_sla_days,
    default_review_frequency: (typeof obj.default_review_frequency === 'string' && obj.default_review_frequency) || DEFAULT_SETTINGS.default_review_frequency,
    auto_populate_lookback_months: (typeof obj.auto_populate_lookback_months === 'number' && obj.auto_populate_lookback_months > 0) ? obj.auto_populate_lookback_months : DEFAULT_SETTINGS.auto_populate_lookback_months,
    brand_primary_color: (typeof obj.brand_primary_color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(obj.brand_primary_color)) ? obj.brand_primary_color : DEFAULT_SETTINGS.brand_primary_color,
    brand_accent_color: (typeof obj.brand_accent_color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(obj.brand_accent_color)) ? obj.brand_accent_color : DEFAULT_SETTINGS.brand_accent_color,
    brand_header_text_color: (typeof obj.brand_header_text_color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(obj.brand_header_text_color)) ? obj.brand_header_text_color : DEFAULT_SETTINGS.brand_header_text_color,
    report_footer_text: (typeof obj.report_footer_text === 'string' && obj.report_footer_text) || DEFAULT_SETTINGS.report_footer_text,
  };
}

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  ai_enabled: false,
  inspections_enabled: false,
  design_dev_enabled: false,
  complaints_enabled: false,
};

interface OrgState {
  organization: Organization | null;
  settings: OrgSettings;
  featureFlags: FeatureFlags;

  setOrganization: (org: Organization | null) => void;
  updateSettings: (settings: Partial<OrgSettings>) => void;
  updateFeatureFlags: (flags: Partial<FeatureFlags>) => void;
  reset: () => void;
}

/** Extract OrgBranding from organization + normalized settings */
export function getOrgBranding(org: Organization | null, settings: OrgSettings): OrgBranding {
  return {
    name: org?.name ?? '',
    logo_url: org?.logo_url ?? null,
    address: org?.address ?? null,
    phone: org?.phone ?? null,
    email: org?.email ?? null,
    website: org?.website ?? null,
    tax_id: org?.tax_id ?? null,
    primary_color: settings.brand_primary_color,
    accent_color: settings.brand_accent_color,
    header_text_color: settings.brand_header_text_color,
    footer_text: settings.report_footer_text,
  };
}

export const useOrgStore = create<OrgState>((set) => ({
  organization: null,
  settings: DEFAULT_SETTINGS,
  featureFlags: DEFAULT_FEATURE_FLAGS,

  setOrganization: (organization) => {
    set({
      organization,
      settings: normalizeSettings(organization?.settings),
      featureFlags: organization?.feature_flags ?? DEFAULT_FEATURE_FLAGS,
    });
  },

  updateSettings: (partial) => {
    set((state) => ({
      settings: { ...state.settings, ...partial },
    }));
  },

  updateFeatureFlags: (partial) => {
    set((state) => ({
      featureFlags: { ...state.featureFlags, ...partial },
    }));
  },

  reset: () => {
    set({
      organization: null,
      settings: DEFAULT_SETTINGS,
      featureFlags: DEFAULT_FEATURE_FLAGS,
    });
  },
}));
